import React, { useState, useRef } from 'react';

const BookListDisplay = ({ studentId, booksData, bookListsData, bookListItemsData, studentName }) => {
    // v2.0 - Ultra-flat design with dark popovers for ReadingPlex
    const [expandedBookLists, setExpandedBookLists] = useState({});
    const [showAllBooks, setShowAllBooks] = useState({});
    const popoverRef = useRef({});

    if (!booksData || !bookListsData || !bookListItemsData) {
        return (
            <div className="p-2 text-xs text-gray-600 italic">
                No reading data available
            </div>
        );
    }

    // Filter book lists for this student
    const studentBookLists = bookListsData.filter(bl =>
        bl.student === studentId && parseInt(bl.active) === 1
    );
    const completedBookLists = bookListsData.filter(bl =>
        bl.student === studentId && parseInt(bl.active) === 0
    );

    // Create book lookup map
    const bookMap = {};
    booksData.forEach(book => {
        bookMap[book.id] = book;
    });

    // Get books for a specific book list
    const getBooksForList = (listId) => {
        return bookListItemsData.filter(item => item.book_list_id === listId);
    };

    // Calculate reading progress
    const calculateProgress = (listId) => {
        const items = getBooksForList(listId);
        const total = items.length;
        const completed = items.filter(item => item.date_completed !== null && item.date_completed !== undefined).length;
        return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
    };

    const toggleBookList = (listId) => {
        setExpandedBookLists(prev => ({
            ...prev,
            [listId]: !prev[listId]
        }));
    };

    const toggleAllBooks = (listId) => {
        setShowAllBooks(prev => ({
            ...prev,
            [listId]: !prev[listId]
        }));
    };

    return (
        <div className="space-y-2">
            {/* Active Book Lists */}
            {studentBookLists.length > 0 && studentBookLists.map(bookList => {
                const items = getBooksForList(bookList.id);
                const progress = calculateProgress(bookList.id);
                const assignedBooks = items.filter(item =>
                    item.date_assigned !== null &&
                    (!item.date_completed || item.date_completed === null)
                );
                const completedBooks = items.filter(item =>
                    item.date_completed !== null
                );
                const isPopoverOpen = showAllBooks[bookList.id] || false;

                return (
                    <div key={bookList.id} className="border-l-4 border-teal-600 rounded shadow-md bg-white py-0.5 px-2" style={{ position: 'relative', overflow: 'visible' }}>
                        {/* Active emphasis: bold text + colored underline */}
                        <div className="flex items-center justify-between">
                            <div className="text-base font-extrabold text-teal-600 flex-1 truncate border-b-2 border-teal-600" style={{ lineHeight: '1.1', paddingBottom: '2px' }}>{bookList.name || 'Reading List'}</div>
                            {/* Percentage - Click to show all books */}
                            <div className="relative z-10" ref={el => popoverRef.current[bookList.id] = el}>
                                <button
                                    onClick={() => toggleAllBooks(bookList.id)}
                                    className="text-base font-bold text-teal-600 hover:text-teal-800 cursor-pointer underline decoration-dotted"
                                    title="Click to view all books"
                                >
                                    {progress.percentage}%
                                </button>

                                {/* Click-triggered popover - BLACK background, WHITE text, OVERLAPS frames */}
                                {/* Mobile: narrower + fit viewport, Desktop: right-aligned */}
                                {isPopoverOpen && (
                                    <div className="absolute right-0 sm:right-0 top-full mt-1 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-black rounded-lg shadow-2xl border-4 border-teal-400 z-[100]">
                                        <div className="px-3 py-2 border-b-2 border-teal-400 bg-gray-900">
                                            <div className="text-sm font-bold text-white">{bookList.name || 'Reading List'}</div>
                                        </div>
                                        <div className="max-h-[48rem] overflow-y-auto p-3 bg-black">
                                            {items.length > 0 ? items.map(item => {
                                                const book = bookMap[item.book_id];
                                                const isCompleted = item.date_completed !== null && item.date_completed !== undefined;
                                                return (
                                                    <div key={item.id} className="text-sm py-0.5 font-normal text-white">
                                                        <span className={`font-bold text-base mr-1 ${isCompleted ? 'text-green-400' : 'text-orange-400'}`}>
                                                            {isCompleted ? '✓' : '○'}
                                                        </span>
                                                        {book ? book.title : `Book ${item.book_id}`}
                                                    </div>
                                                );
                                            }) : (
                                                <div className="text-white text-sm py-2">No books found</div>
                                            )}
                                        </div>
                                        <div className="px-3 py-2 border-t-2 border-teal-400 text-sm text-white font-bold bg-gray-900">
                                            {progress.completed} of {progress.total} completed
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Progress bar below name */}
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                            <div
                                className="bg-teal-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progress.percentage}%` }}
                            ></div>
                        </div>

                        {/* Books section - Assigned ALWAYS visible, Completed collapsible */}
                        <div className="bg-white">
                            {/* Assigned Books - ALWAYS VISIBLE */}
                            {assignedBooks.length > 0 && (
                                <div className="border-t border-gray-200 bg-orange-50">
                                    <div className="px-2 py-0.5">
                                        <div className="text-xs font-bold text-orange-700">
                                            📚 Assigned ({assignedBooks.length})
                                        </div>
                                    </div>
                                    <div className="px-2 pb-1 space-y-0.5">
                                        {assignedBooks.map(item => {
                                            const book = bookMap[item.book_id];
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="text-xs py-0.5 px-1 bg-white border-l-2 border-orange-500"
                                                >
                                                    {book ? book.title : `Book ${item.book_id}`}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Completed Books - Collapsible, VERY FLAT */}
                            {completedBooks.length > 0 && (
                                <div className="border-t border-gray-200">
                                    <button
                                        onClick={() => toggleBookList(bookList.id)}
                                        className="w-full flex items-center justify-between px-2 py-0.5 hover:bg-green-50 active:bg-green-50 transition-colors touch-manipulation text-left"
                                    >
                                        <span className="text-xs font-bold text-green-700">
                                            {expandedBookLists[bookList.id] ? '▼' : '▶'} ✅ Completed
                                        </span>
                                        <span className="bg-green-100 text-green-800 font-bold px-1.5 py-0.5 rounded text-xs">
                                            {completedBooks.length}
                                        </span>
                                    </button>
                                    {expandedBookLists[bookList.id] && (
                                        <div className="px-2 pb-1 space-y-0.5 bg-green-50">
                                            {completedBooks.map(item => {
                                                const book = bookMap[item.book_id];
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="text-xs py-0.5 px-1 bg-white border-l-2 border-green-500"
                                                    >
                                                        {book ? book.title : `Book ${item.book_id}`}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* No books message - FLAT */}
                            {assignedBooks.length === 0 && completedBooks.length === 0 && (
                                <div className="px-2 py-1 text-center border-t border-gray-200">
                                    <p className="text-gray-600 italic text-xs">No books assigned yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Completed Book Lists - ULTRA FLAT */}
            {completedBookLists.length > 0 && (
                <details className="border border-gray-300 rounded-md overflow-hidden bg-gray-100">
                    <summary className="cursor-pointer px-2 py-0.5 hover:bg-gray-200 active:bg-gray-200 transition-colors touch-manipulation">
                        <span className="text-xs font-bold text-gray-700">
                            ▶ 📦 Completed Lists ({completedBookLists.length})
                        </span>
                    </summary>
                    <div className="bg-white divide-y divide-gray-200">
                        {completedBookLists.map(bookList => {
                            const items = getBooksForList(bookList.id);
                            const progress = calculateProgress(bookList.id);
                            const isExpanded = expandedBookLists[bookList.id] || false;

                            return (
                                <div key={bookList.id}>
                                    <button
                                        onClick={() => toggleBookList(bookList.id)}
                                        className="w-full flex items-center justify-between px-2 py-0.5 hover:bg-gray-50 active:bg-gray-50 text-left text-xs touch-manipulation"
                                    >
                                        <div className="flex items-center gap-1 flex-1 min-w-0">
                                            <span className="text-gray-600 font-bold">
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                            <div className="font-semibold text-gray-800 truncate">{bookList.name || 'Reading List'}</div>
                                        </div>
                                        <div className="px-1.5 py-0.5 bg-green-100 text-green-800 font-bold rounded text-xs flex-shrink-0">
                                            {progress.percentage}%
                                        </div>
                                    </button>

                                    {isExpanded && items.length > 0 && (
                                        <div className="px-2 pb-1.5 bg-gray-50 space-y-0.5">
                                            {items.map(item => {
                                                const book = bookMap[item.book_id];
                                                const isCompleted = item.date_completed !== null && item.date_completed !== undefined;
                                                return (
                                                    <div key={item.id} className="flex items-center gap-1 p-0.5 text-xs">
                                                        <div className={`w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                                            }`}>
                                                            {isCompleted && <span className="text-white text-xs">✓</span>}
                                                        </div>
                                                        <div className="flex-1 text-gray-800">
                                                            {book ? book.title : `Book ${item.book_id}`}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </details>
            )}

            {/* No Book Lists */}
            {studentBookLists.length === 0 && completedBookLists.length === 0 && (
                <div className="p-2 bg-gray-50 rounded border border-gray-300 text-center">
                    <p className="text-xs text-gray-600">No reading lists assigned</p>
                </div>
            )}
        </div>
    );
};

export default BookListDisplay;

